import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { registrationService } from '../services/registrationService';
import { configService } from '../services/configService';

const { FiRefreshCw, FiLock, FiSettings, FiUsers, FiSave, FiAlertCircle, FiLoader, FiStar } = FiIcons;

const ADMIN_PASSWORD = "1234cgbsgnffBdfgH34VxdsghNd852W";

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('regs');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [registrations, setRegistrations] = useState([]);
  
  // Local State สำหรับเก็บค่าที่กำลังพิมพ์ (ยังไม่เซฟลง DB)
  const [draftConfig, setDraftConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const authStatus = sessionStorage.getItem('admin_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [regs, conf] = await Promise.all([
        registrationService.getAll(),
        configService.getAll()
      ]);
      setRegistrations(regs);
      setDraftConfig(conf); // โหลดค่าจาก DB มาไว้ใน Draft
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ฟังก์ชันสำหรับเซฟทุกอย่างลง Google Sheets
  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      await configService.bulkUpdate(draftConfig);
      alert('บันทึกข้อมูลทั้งหมดลงฐานข้อมูลเรียบร้อยแล้ว!');
      fetchData(); // โหลดข้อมูลใหม่เพื่อยืนยัน
    } catch (error) {
      console.error(error);
      alert('เกิดข้อผิดพลาดในการบันทึก: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (key, value) => {
    setDraftConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_authenticated', 'true');
      fetchData();
    } else {
      alert('รหัสผ่านไม่ถูกต้อง');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full bg-zinc-900 border-2 border-zinc-800 p-10 shadow-2xl text-center">
          <SafeIcon icon={FiLock} className="w-12 h-12 text-brand-red mx-auto mb-6" />
          <h2 className="text-2xl font-black text-white mb-8 uppercase tracking-tighter">Admin Access</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <input 
              type="password" 
              placeholder="Password" 
              className="w-full bg-zinc-800 border-2 border-zinc-700 p-4 text-white focus:border-brand-red outline-none text-center font-bold" 
              value={passwordInput} 
              onChange={(e) => setPasswordInput(e.target.value)} 
              autoFocus 
            />
            <button className="w-full py-4 bg-brand-red text-white font-black uppercase tracking-widest hover:bg-white hover:text-zinc-900 transition-all">Login</button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-zinc-50 pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12">
          <div className="flex gap-2 bg-white p-1 border-2 border-zinc-200">
            <button onClick={() => setActiveTab('regs')} className={`px-8 py-3 font-black uppercase tracking-widest text-[10px] flex items-center gap-2 transition-all ${activeTab === 'regs' ? 'bg-zinc-900 text-white' : 'text-zinc-400 hover:text-zinc-600'}`}>
              <SafeIcon icon={FiUsers} /> รายชื่อผู้สมัคร
            </button>
            <button onClick={() => setActiveTab('settings')} className={`px-8 py-3 font-black uppercase tracking-widest text-[10px] flex items-center gap-2 transition-all ${activeTab === 'settings' ? 'bg-zinc-900 text-white' : 'text-zinc-400 hover:text-zinc-600'}`}>
              <SafeIcon icon={FiSettings} /> ตั้งค่าแคมเปญ
            </button>
          </div>

          <div className="flex items-center gap-4">
            {activeTab === 'settings' && (
              <button 
                onClick={handleSaveAll}
                disabled={isSaving}
                className="flex items-center gap-3 px-10 py-4 bg-brand-red text-white font-black uppercase tracking-widest text-sm hover:bg-zinc-900 transition-all shadow-xl disabled:opacity-50"
              >
                {isSaving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
                {isSaving ? 'Saving...' : 'Save All Settings'}
              </button>
            )}
            <button onClick={fetchData} className="p-4 bg-white border-2 border-zinc-200 hover:bg-zinc-50 transition-colors">
              <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {activeTab === 'regs' ? (
          <div className="bg-white border-2 border-zinc-900 shadow-[20px_20px_0px_0px_rgba(0,0,0,0.05)] overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-zinc-900 text-white uppercase text-[10px] font-black tracking-[0.2em]">
                  <th className="p-6">Time</th>
                  <th className="p-6">Student Info</th>
                  <th className="p-6">Method</th>
                  <th className="p-6">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {registrations.map(item => (
                  <tr key={item.id} className="hover:bg-zinc-50">
                    <td className="p-6 text-xs font-bold text-zinc-400">{new Date(item.created_at).toLocaleString('th-TH')}</td>
                    <td className="p-6">
                      <div className="font-black text-zinc-900">{item.fullName} ({item.nickname})</div>
                      <div className="text-xs text-zinc-500 flex gap-4 mt-1">
                        <span>{item.phone}</span>
                        <span>{item.email}</span>
                      </div>
                    </td>
                    <td className="p-6"><span className="text-[10px] font-black uppercase bg-zinc-100 px-2 py-1">{item.paymentMethod}</span></td>
                    <td className="p-6">
                      <select 
                        value={item.status} 
                        onChange={(e) => registrationService.updateStatus(item.id, e.target.value).then(fetchData)}
                        className={`px-4 py-1 text-[10px] font-black uppercase tracking-widest border-2 outline-none cursor-pointer ${item.status === 'Paid' ? 'border-green-500 text-green-600 bg-green-50' : 'border-amber-500 text-amber-600 bg-amber-50'}`}
                      >
                        <option value="pending_verification">Pending</option>
                        <option value="Paid">Paid</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white border-2 border-zinc-900 p-10 shadow-[20px_20px_0px_0px_rgba(0,0,0,0.05)]">
                <div className="flex items-center justify-between mb-10 border-b-2 border-zinc-100 pb-4">
                  <h3 className="font-black uppercase tracking-[0.2em] text-sm flex items-center gap-3">
                    <SafeIcon icon={FiStar} className="text-brand-red" /> Pricing Tier Timeline
                  </h3>
                  <div className="flex items-center gap-2 text-[10px] font-black text-amber-600 bg-amber-50 px-3 py-1">
                    <SafeIcon icon={FiAlertCircle} /> อย่าลืมกดปุ่ม Save All ด้านบนหลังจากแก้ไข
                  </div>
                </div>
                
                <div className="space-y-10">
                  {[1, 2, 3, 4, 5].map(num => (
                    <div key={num} className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end p-6 bg-zinc-50 border border-zinc-100 rounded-lg">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest mb-3 text-zinc-400">Tier {num} Price (THB)</label>
                        <input 
                          type="text" 
                          value={draftConfig[`tier${num}_price`] || ''} 
                          onChange={(e) => handleInputChange(`tier${num}_price`, e.target.value)}
                          className="w-full p-4 bg-white border-2 border-zinc-200 font-black text-xl text-zinc-900 outline-none focus:border-brand-red transition-all" 
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest mb-3 text-zinc-400">Tier {num} Deadline</label>
                        <input 
                          type="datetime-local" 
                          value={draftConfig[`tier${num}_date`]?.slice(0, 16) || ''} 
                          onChange={(e) => handleInputChange(`tier${num}_date`, e.target.value)}
                          className="w-full p-4 bg-white border-2 border-zinc-200 font-black text-sm text-zinc-900 outline-none focus:border-brand-red transition-all" 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-white border-2 border-zinc-900 p-10 shadow-[20px_20px_0px_0px_rgba(0,0,0,0.1)]">
                <h3 className="font-black uppercase tracking-[0.2em] text-sm mb-8 border-b-2 border-brand-red pb-4 flex items-center gap-2">
                  <SafeIcon icon={FiStar} className="text-brand-red" /> Featured Price Box
                </h3>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest mb-3 text-zinc-400">Main Pricing Display (THB)</label>
                  <input 
                    type="text" 
                    value={draftConfig.featured_price || ''} 
                    onChange={(e) => handleInputChange('featured_price', e.target.value)}
                    className="w-full p-4 bg-zinc-900 text-brand-red border-2 border-zinc-900 font-black text-3xl outline-none" 
                  />
                  <p className="text-[9px] text-zinc-400 mt-4 leading-relaxed font-bold uppercase italic">
                    * เมื่อตั้งค่าเป็น Auto ระบบจะเลือกราคาจาก Tier ที่ Active มาแสดงที่นี่โดยอัตโนมัติ
                  </p>
                </div>
              </div>

              <div className="bg-white border-2 border-zinc-900 p-10 shadow-[20px_20px_0px_0px_rgba(0,0,0,0.05)]">
                <h3 className="font-black uppercase tracking-[0.2em] text-sm mb-8 border-b-2 border-zinc-100 pb-4">Global Controls</h3>
                <div className="space-y-8">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest mb-3 text-zinc-400">Seat Offset</label>
                    <input 
                      type="number" 
                      value={draftConfig.seat_offset || '0'} 
                      onChange={(e) => handleInputChange('seat_offset', e.target.value)}
                      className="w-full p-4 bg-zinc-50 border-2 border-zinc-200 font-black text-2xl text-brand-red outline-none" 
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest mb-3 text-zinc-400">Manual Tier Override</label>
                    <select 
                      value={draftConfig.manual_tier || 'auto'} 
                      onChange={(e) => handleInputChange('manual_tier', e.target.value)}
                      className="w-full p-4 bg-zinc-900 text-white border-2 border-zinc-900 font-black uppercase tracking-widest text-xs outline-none cursor-pointer"
                    >
                      <option value="auto">Automatic (By Date)</option>
                      <option value="1">Force Tier 1</option>
                      <option value="2">Force Tier 2</option>
                      <option value="3">Force Tier 3</option>
                      <option value="4">Force Tier 4</option>
                      <option value="5">Force Tier 5</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}